"use client"

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { AppSidebar } from './AppSidebar'
import { AppHeader } from './AppHeader'
import { cn } from '@/lib/utils'
import { WorkAttentionMonitor } from './dashboard/WorkAttentionMonitor'

interface AuthedLayoutProps {
  children: React.ReactNode
}

export default function AuthedLayout({ children }: AuthedLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isImpersonating, setIsImpersonating] = useState(false)
  const pathname = usePathname()
  const isInitialRender = useRef(true)

  const { user, logout, isLoading } = useAuth()

  useEffect(() => {
    setIsImpersonating(Boolean(localStorage.getItem('impersonationOriginalAccessToken')))
  }, [])

  const stopImpersonation = () => {
    const originalAccessToken = localStorage.getItem('impersonationOriginalAccessToken')
    const originalRefreshToken = localStorage.getItem('impersonationOriginalRefreshToken')

    if (originalAccessToken) {
      localStorage.setItem('accessToken', originalAccessToken)
    }
    if (originalRefreshToken) {
      localStorage.setItem('refreshToken', originalRefreshToken)
    } else {
      localStorage.removeItem('refreshToken')
    }
    localStorage.removeItem('impersonationOriginalAccessToken')
    localStorage.removeItem('impersonationOriginalRefreshToken')
    window.location.href = '/users/list'
  }

  // Skip redirect on initial render - let React Query complete its first fetch
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    
    // Only redirect if we're past the initial render AND there's no user
    if (!isLoading && !user) {
      console.log('[AuthedLayout] redirecting to login')
      window.location.href = '/login'
    }
  }, [user, isLoading])

  if (isLoading || !user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-lg">Loading...</p>
      </div>
    )
  }

  return (
    <>
      <WorkAttentionMonitor />
      <div className="flex h-screen bg-background">
        <AppSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          user={user}
          onLogout={logout}
        />

        <div className={cn(
          "flex flex-1 flex-col overflow-hidden transition-all duration-300",
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
        )}>
          <AppHeader
            onMenuClick={() => setSidebarOpen(true)}
            user={user}
            onLogout={logout}
          />

          {isImpersonating && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              <div className="mx-auto flex max-w-[1680px] items-center justify-between gap-3">
                <span>
                  Impersonating {user.name || user.email}. Admin actions are audited.
                </span>
                <button
                  type="button"
                  onClick={stopImpersonation}
                  className="rounded-md border border-amber-300 bg-white px-3 py-1 font-medium text-amber-900 hover:bg-amber-100"
                >
                  Return to admin
                </button>
              </div>
            </div>
          )}

          <main className="flex-1 overflow-y-auto bg-background px-3 py-4 sm:px-4 md:px-6 lg:px-8">
            <div className={cn(
              "mx-auto w-full max-w-[1680px]",
              pathname === '/dashboard' ? "space-y-6" : "space-y-4"
            )}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
